import React from 'react';
import { DayPicker } from 'react-day-picker';
import { motion, AnimatePresence } from 'framer-motion';
import { CalendarDay } from './atomic/CalendarDay';

interface SmartCalendarProps {
    currentMonth: Date;
    onMonthChange: (date: Date) => void;
    selectedDate: Date;
    onSelectDate: (date: Date) => void;
    getDateStyle?: (date: Date) => {
        color?: string;
        fontWeight?: string | number;
        opacity?: number;
        markers?: Array<{ type: string; status: string }>;
    };
}

/**
 * SmartCalendar: The core reusable calendar grid.
 * Handles styling, animations, and date selection.
 * REFACTORED: Day component extracted for better SoC.
 */
export const SmartCalendar: React.FC<SmartCalendarProps> = ({
    currentMonth,
    onMonthChange,
    selectedDate,
    onSelectDate,
    getDateStyle
}) => {
    const [[monthKey, direction], setMonthState] = React.useState<[string, number]>([currentMonth.toISOString(), 0]);

    React.useEffect(() => {
        const newKey = currentMonth.toISOString();
        if (newKey !== monthKey) {
            const dir = new Date(newKey) > new Date(monthKey) ? 1 : -1;
            setMonthState([newKey, dir]);
        }
    }, [currentMonth, monthKey]);

    const handleSwipe = (dir: 'left' | 'right') => {
        const nextMonth = new Date(currentMonth);
        const nextDir = dir === 'left' ? 1 : -1;
        if (dir === 'left') nextMonth.setMonth(nextMonth.getMonth() + 1);
        else nextMonth.setMonth(nextMonth.getMonth() - 1);
        setMonthState([nextMonth.toISOString(), nextDir]);
        onMonthChange(nextMonth);
    };

    const variants = {
        enter: (direction: number) => ({ x: direction > 0 ? 50 : -50, opacity: 0 }),
        center: { x: 0, opacity: 1 },
        exit: (direction: number) => ({ x: direction < 0 ? 50 : -50, opacity: 0 })
    };

    return (
        <div style={{ position: 'relative', width: '100%', overflow: 'hidden' }}>
            <AnimatePresence initial={false} custom={direction} mode="popLayout">
                <motion.div 
                    key={monthKey}
                    custom={direction}
                    variants={variants}
                    initial="enter" animate="center" exit="exit"
                    transition={{ x: { type: "spring", stiffness: 300, damping: 30 }, opacity: { duration: 0.2 } }}
                    style={{ width: '100%', display: 'flex', justifyContent: 'center', touchAction: 'pan-y' }}
                    onPanEnd={(_e, info) => {
                        const swipeThreshold = 50;
                        if (info.offset.x < -swipeThreshold) handleSwipe('left');
                        else if (info.offset.x > swipeThreshold) handleSwipe('right');
                    }}
                >
                    <style>{`
                        .rdp-root { --rdp-accent-color: #f29b11 !important; --rdp-today-color: #2ecc71 !important; margin: 0; } 
                        .rdp-month_caption { display: flex !important; align-items: center !important; justify-content: center !important; position: relative !important; margin-bottom: 10px !important; height: 40px !important; width: 100% !important; }
                        .rdp-caption_label { color: #f29b11 !important; font-weight: 900 !important; text-transform: uppercase !important; font-size: 1rem !important; z-index: 1; } 
                        .rdp-nav { position: absolute !important; top: 0 !important; left: 0 !important; right: 0 !important; display: flex !important; justify-content: space-between !important; align-items: center !important; width: 100% !important; height: 40px !important; pointer-events: auto !important; z-index: 10 !important; padding: 0 5px !important; }
                        .rdp-button_next, .rdp-button_previous { color: #f29b11 !important; background: transparent !important; border: none !important; cursor: pointer !important; display: flex !important; align-items: center !important; justify-content: center !important; width: 32px !important; height: 32px !important; } 
                        .rdp-button_next svg, .rdp-button_previous svg, .rdp-chevron { fill: #f29b11 !important; stroke: #f29b11 !important; }
                        .rdp-day_selected { background-color: #f29b11 !important; color: #000 !important; border-radius: 50% !important; } 
                        .rdp-day_today { color: #2ecc71 !important; font-weight: 900 !important; } 
                        .rdp-head_cell { color: #f29b11 !important; font-weight: 900 !important; text-transform: uppercase !important; } 
                        .rdp-day_outside { color: rgba(242, 155, 17, 0.2) !important; }
                    `}</style>
                    <DayPicker 
                        mode="single" month={currentMonth} onMonthChange={onMonthChange}
                        selected={selectedDate} onSelect={(d) => d && onSelectDate(d)} weekStartsOn={1} 
                        components={{ 
                            Day: (dayProps) => { 
                                const { day, modifiers } = dayProps; 
                                if (!day) return <></>; 
                                const date = day.date; 
                                const isSelected = !!modifiers.selected; 
                                let status = getDateStyle ? getDateStyle(date) : { color: '#666', fontWeight: '500', opacity: 1, markers: [] };

                                return (
                                    <CalendarDay 
                                        date={date} 
                                        isSelected={isSelected} 
                                        onSelect={onSelectDate} 
                                        status={status as any} 
                                    />
                                ); 
                            } 
                        }} 
                    />
                </motion.div>
            </AnimatePresence>
        </div>
    );
};
